/**
 * Which of the composer's four modes is showing while a node is selected — Ask,
 * Reason, Investigate or Record (D347).
 *
 * The node card reads; the composer acts. Every press the card used to carry
 * lives in one of these modes now, at the foot of the column where the input
 * already was, so a reader scrolled deep into a card still has every act one
 * row away. The mode is the reader's: it outlives a change of selection — a
 * reader checking claim after claim stays in Investigate — and it starts on Ask,
 * the composer's own first job.
 *
 * Module state rather than the store's, `adjust.ts`'s reason: two readers that
 * are not parent and child (the composer and the card's empty Sources meter,
 * which opens Investigate), and a view choice that dies with the tab has no
 * business in framing or persistence. No key acts through it: D, C, V and the
 * rest act on the selection from App's handler whatever mode is showing.
 */

import { useSyncExternalStore } from 'react'

export type ComposerMode = 'ask' | 'reason' | 'investigate' | 'record'

export const COMPOSER_MODES: readonly ComposerMode[] = ['ask', 'reason', 'investigate', 'record']

/** The modes' names, as their tabs print them. */
export const MODE_LABEL: Record<ComposerMode, string> = {
  ask: 'Ask',
  reason: 'Reason',
  investigate: 'Investigate',
  record: 'Record',
}

let mode: ComposerMode = 'ask'
const watchers = new Set<() => void>()

export function composerMode(): ComposerMode {
  return mode
}

export function setComposerMode(next: ComposerMode): void {
  if (next === mode) return
  mode = next
  for (const w of watchers) w()
}

function onModeChange(fn: () => void): () => void {
  watchers.add(fn)
  return () => watchers.delete(fn)
}

export function useComposerMode(): ComposerMode {
  return useSyncExternalStore(onModeChange, composerMode, composerMode)
}

/**
 * The modes a node offers. The seed has no Record: nothing is recorded against
 * the question itself, and the seed is never rewritten (its words are the
 * reader's). A mode the node does not offer shows as Ask — the reader's choice
 * is kept for the next node that offers it, rather than overwritten by this one.
 */
export function modesFor(node: { parentId: string | null }): readonly ComposerMode[] {
  return node.parentId === null ? COMPOSER_MODES.filter((m) => m !== 'record') : COMPOSER_MODES
}

export function shownMode(node: { parentId: string | null }, chosen: ComposerMode): ComposerMode {
  return modesFor(node).includes(chosen) ? chosen : 'ask'
}
